import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  GetSocialProfileParams,
  LoginData,
  LoginParams,
  ResetPasswordParams,
  SignUpData,
  SignUpParams,
} from 'features/auth/domain/AuthRepositoryInterface';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useAmplitude } from 'src/context/AmplitudeProvider';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import AuthRepository from 'src/features/auth/data/AuthRepository';
import { ResetPasswordResponse } from 'src/features/auth/data/service/authService.types';
import LoginUseCase from 'src/features/auth/domain/useCases/LoginUseCase';
import SignUpUseCase from 'src/features/auth/domain/useCases/SignUpUseCase';
import GetSocialSignUpProfileUseCase from 'src/features/auth/domain/useCases/SocialSignUpUseCase';
import {
  GetSocialSignUpProfileUseCaseInterface,
  LoginUseCaseInterface,
  SignUpUseCaseInterface,
} from 'src/features/auth/domain/useCases/types';
import useTrackAuthEvents from 'src/features/auth/presentation/hooks/useTrackAuthEvents';
import { auth, providers } from 'src/firebase-config';
import { diContainer } from 'src/inversify.config';
import { UserType } from 'src/types/common';
import growthbook from 'src/utils/growthBook/growthBook';

type ResetPasswordError = {
  email: string[];
};

const authRepository = diContainer.get<AuthRepository>(AuthRepository);
const loginUseCase = diContainer.get<LoginUseCaseInterface>(LoginUseCase);
const signUpUseCase = diContainer.get<SignUpUseCaseInterface>(SignUpUseCase);
const getSocialProfileUseCase =
  diContainer.get<GetSocialSignUpProfileUseCaseInterface>(
    GetSocialSignUpProfileUseCase
  );

export default function useAuthMutations() {
  const { setIsLoading } = useAppUI();
  const { setSession } = useAuthContext();
  const { setAmplitudeUser, resetAmplitudeUser } = useAmplitude();
  const { trackLogin, trackRegister } = useTrackAuthEvents();
  const navigate = useNavigate();
  const { alertNotification } = useAppUI();

  const handleAmplitudeUserChange = ({ user }: SignUpData | LoginData) => {
    setAmplitudeUser({
      email: user.email,
      phone: user.phone_number,
      name: user.first_name,
      last_name: user.last_name,
      user_id: user.pk,
      is_b2b: user.type === UserType.B2b,
      document_number: user.profile.document_number,
      document_type: user.profile.document_type,
    });
  };

  const loginMutation = useMutation({
    mutationFn: (params: LoginParams) => loginUseCase.execute(params),
    onSuccess: (data) => {
      setSession?.(data);
      handleAmplitudeUserChange(data);
      trackLogin(data);
      growthbook.setAttributes({ id: data.user.pk });
      navigate('/', { replace: true });
    },
    mutationKey: ['login'],
  });

  const registerMutation = useMutation({
    mutationFn: (params: SignUpParams) => signUpUseCase.execute(params),
    onSuccess: (data) => {
      setSession?.(data);
      handleAmplitudeUserChange(data);
      trackRegister(data);
    },
    mutationKey: ['register'],
  });

  const logoutMutation = useMutation({
    mutationFn: () => {
      return authRepository.logOut();
    },
    onSettled: () => {
      setSession?.(undefined);
      resetAmplitudeUser();
      navigate('/');
    },
    mutationKey: ['logout'],
  });

  const resetPasswordMutation = useMutation<
    ResetPasswordResponse,
    AxiosError<ResetPasswordError>,
    ResetPasswordParams,
    unknown
  >({
    mutationFn: (params: ResetPasswordParams) =>
      authRepository.resetPassword(params),
    mutationKey: ['resetPassword'],
  });

  const getSocialProfileMutation = useMutation({
    mutationFn: (params: GetSocialProfileParams) =>
      getSocialProfileUseCase.execute(params),
    mutationKey: ['getSocialProfile'],
  });

  const signInWithSocialMediaMutation = useMutation({
    mutationFn: async (providerName: 'google' | 'facebook' | 'apple') => {
      const provider = providers[providerName];

      const currentUser = auth.currentUser;
      try {
        if (currentUser) {
          const loginData = await loginMutation.mutateAsync({
            email: currentUser.email as string,
            social_network: { name: providerName, uid: currentUser.uid },
          });
          handleAmplitudeUserChange(loginData);
          trackLogin(loginData);

          return loginData;
        } else {
          const credentials = await provider();

          const user = credentials.user;
          const loginData = await loginMutation.mutateAsync({
            email: user.email as string,
            social_network: { name: providerName, uid: user.uid },
          });
          handleAmplitudeUserChange(loginData);
          trackLogin(loginData);

          return loginData;
        }
      } catch (error) {
        console.error('Error al iniciar sesión:', error);
        if (auth.currentUser) {
          signOut(auth);
        }
        throw new Error(
          `No se encontró una cuenta asociada con ${providerName}`
        );
      }
    },
    mutationKey: ['signInWithSocialMedia'],
    onMutate: () => {
      setIsLoading(true);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const registerWithSocialMediaMutation = useMutation({
    mutationFn: async (providerName: 'google' | 'facebook' | 'apple') => {
      const provider = providers[providerName];
      try {
        const userCredential = await provider();
        return userCredential;
      } catch (error) {
        console.error(`Error en el registro con ${providerName}: `, error);
        if (auth.currentUser) {
          signOut(auth);
        }
        throw error;
      }
    },
    mutationKey: ['registerWithSocialMedia'],
    onMutate: () => {
      setIsLoading(true);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const registerJwtMutation = useMutation({
    mutationFn: (params: { token: string }) =>
      authRepository.registerJwt(params),
    mutationKey: ['registerJwt'],
  });

  return {
    loginMutation,
    registerMutation,
    logoutMutation,
    resetPasswordMutation,
    signInWithSocialMediaMutation,
    registerWithSocialMediaMutation,
    getSocialProfileMutation,
    registerJwtMutation,
  };
}
